import { useState } from 'react'
import { CommissionGrid, useCommissionPlans } from '@/components/CommissionGrid'
import { useCountry } from '@/lib/country'
import { ErrorState, Loading } from '@/components/ui/primitives'
import { FilterSelect } from '@/components/ui/listing'

/**
 * What this product pays a partner who has no rate of their own for it.
 *
 * <p>
 * Here rather than on each reseller, because what a product pays is a property of the product.
 * Typed once per partner per country it becomes one commercial decision copied many ways, and
 * the first copy somebody forgets to update is a partner quietly on last year's number.
 * </p>
 * <p>
 * Country-scoped, like the pricing beside it. A grid is drafted and then activated, because a
 * standard covers every partner in a country and is therefore a larger money decision than any
 * one partner's plan.
 * </p>
 */
export function ProductCommission({ productId }: { productId: string }) {
  const { available, current } = useCountry()
  const [countryId, setCountryId] = useState<string | undefined>(current?.countryId)
  const plans = useCommissionPlans({ kind: 'product', productId })

  if (plans.loading && !plans.data) return <Loading />
  if (plans.error) {
    return <ErrorState title={plans.error.title} detail={plans.error.detail} onRetry={plans.reload} />
  }

  const here = (plans.data ?? []).filter((plan) => plan.countryId === countryId)

  return (
    <>
      <div className="mb-5">
        <FilterSelect label="Country" value={countryId ?? ''} onChange={setCountryId}>
          {available.map((country) => (
            <option key={country.countryId} value={country.countryId}>
              {country.name}
            </option>
          ))}
        </FilterSelect>
      </div>

      <CommissionGrid
        subject={{ kind: 'product', productId }}
        countryId={countryId}
        plans={here}
        onChanged={plans.reload}
        title="Standard rate"
        emptyTitle="No standard rate"
        emptyHint="Without one, a deal containing this product accrues nothing for the partner who sold it - with the reason recorded, but zero. A partner holding a rate of their own for it is unaffected."
        editorDescription="What this product pays a partner who has no rate of their own for it. A partner's custom grid overrides this; everybody else is paid it."
      />
    </>
  )
}
